// src/theme.js
//
// Single source of truth for all brand colors, spacing, and typography.
// Import { Colors, Radius, Typography, Fonts, scaledFontSize } wherever you
// need them so the app stays visually consistent without hunting for magic hex
// values.

import { PixelRatio, useWindowDimensions } from 'react-native';

// React Native's <Text> component scales fontSize automatically via
// allowFontScaling (defaults to true). That means the raw fontSize values in
// Typography tokens below already grow/shrink with the user's accessibility
// font-size setting — no extra math required for normal text.
//
// ─── Reactive font scale (preferred) ──────────────────────────────────────
// useFontScale() returns the current device font scale and re-renders the
// component whenever the user changes it mid-session (iOS Dynamic Type,
// Android font size slider). Use this in components that need to compute
// layout dimensions from font size, rather than calling PixelRatio once.
//
//   import { useFontScale } from '../theme';
//   function MyBadge() {
//     const fontScale = useFontScale();
//     return <Text style={{ fontSize: 10 * fontScale }} allowFontScaling={false}>•</Text>;
//   }
export const useFontScale = () => useWindowDimensions().fontScale;

// ─── Static helper (legacy / non-component contexts) ──────────────────────
// scaledFontSize() reads PixelRatio once — it does NOT update mid-session.
// Prefer useFontScale() inside components. Keep this only for contexts where
// hooks cannot be called (e.g. outside React components).
export const scaledFontSize = (base) => Math.round(base * PixelRatio.getFontScale());

export const Colors = {
  // Backgrounds
  bg:        '#0d0f14', // primary screen background
  surface:   '#171a21', // card / sheet background
  surface2:  '#232838', // secondary surface (pill, icon bg)

  // Accent
  accent:    '#7c9cff', // primary interactive color (buttons, active states)
  accentDim: '#4d5f99', // secondary accent / active tag
  accentTrack: '#2a2f3a', // slider track (inactive)

  // Text
  textPrimary:   '#e7e9ee', // body text
  textBright:    '#ffffff', // headings
  textSecondary: '#9aa5b1', // subtitles, captions
  textMuted:     '#5b6472', // placeholder, inactive icon
  textOnAccent:  '#0d0f14', // text on accent-colored backgrounds

  // Kids section accent (soft violet — distinct from the Sleep-tab blue)
  kidsAccent:    '#a78bfa',
  kidsAccentDim: '#6d4fc2',

  // Borders & overlays
  border:     '#1c2029',
  danger:     '#3a2a2a', // destructive / cancel button background
  dangerText: '#ff8a8a', // text on danger background

  // Status
  warning:    '#f5a623', // warning icon / badge color
};

export const Radius = {
  sm:   10,
  md:   12,
  lg:   14,
  xl:   16,
  pill: 20,
  full: 9999,
};

// Font family tokens. fontsLoaded must be true before using these.
// Falls back to system font automatically when fonts aren't ready.
export const Fonts = {
  regular:    'Inter_400Regular',
  medium:     'Inter_500Medium',
  semiBold:   'Inter_600SemiBold',
  bold:       'Inter_700Bold',
  light:      'Inter_300Light',
};

// Raw font sizes — React Native's Text scales these automatically via
// allowFontScaling=true (the default). Do NOT wrap these in scaledFontSize();
// that would double-apply scaling and oversize text at large accessibility
// settings. See the scaledFontSize() note above for when manual scaling is
// appropriate.
export const Typography = {
  screenTitle:  { fontSize: 24, fontFamily: Fonts.semiBold },
  title:        { fontSize: 16, fontFamily: Fonts.semiBold },
  sectionTitle: { fontSize: 13, fontFamily: Fonts.regular },
  body:         { fontSize: 14, fontFamily: Fonts.regular },
  label:        { fontSize: 13, fontFamily: Fonts.regular },
  caption:      { fontSize: 12, fontFamily: Fonts.regular },
  small:        { fontSize: 11, fontFamily: Fonts.regular },
  button:       { fontSize: 15, fontFamily: Fonts.bold },
};
